import i18n from '@/utils/i18n';
import { Table } from 'reactstrap';
import { OrderFulfillmentDetailFragment } from '../../../../graphql/fragments/orderFulfillmentDetail.generated';
import { ContainerType } from './OrderFulfillmentAddContainerTypes';
import ContainerTypeDisplay from '../../../../components/ContainerType/ContainerTypeDisplay';
import {
  ContainerStationStatus,
  ContainerTypeAmount,
  OrderFulfillmentContainerLocation,
  OrderFulfillmentType,
  TrackableItem,
  ItemTypeCategory,
} from '../../../../graphql/generatedTypes';
import { ItemTypeCategoryWithContainer } from '@/utils/ItemTypes';
import TrackableItemDisplay from '../../../../components/TrackableItem/TrackableItemDisplay';

const getNumberOrFallback = (number: number) => {
  if (number === 0) {
    return '-';
  }

  return String(number);
};

type ContainerTypeCountsType = {
  dirty: number;
  clean: number;
  counted: number;
  countLater: number;
  sealed: number;
  empty: number;
  sum: number;
  type: ItemTypeCategoryWithContainer | 'TRACKABLE_ITEM';
};

type NormalizedContainerManualAmount = OrderFulfillmentContainerLocation & {
  amount?: number;
  sealed?: boolean;
};

const normalizeManualAmount = (
  manualAmounts: ContainerTypeAmount[],
  type?: string,
) => {
  const isDeliveryOFType =
    type?.toUpperCase() === OrderFulfillmentType.Delivery;

  return manualAmounts.map((manualAmount) => {
    return [
      {
        container_id: 'manual_amount',
        container_type_id: manualAmount.container_type_id,
        container_name: 'Manual Amount',
        container_type_name: manualAmount.container_type_name,
        container_type_image: manualAmount.container_type_image,
        amount: manualAmount.amount,
        sealed: isDeliveryOFType,
        isEditable: true,
      },
      {
        container_id: 'manual_amount_sealed',
        container_type_id: manualAmount.container_type_id,
        container_name: 'Manual Amount Sealed',
        container_type_name: manualAmount.container_type_name,
        container_type_image: manualAmount.container_type_image,
        amount: manualAmount.amount_sealed,
        sealed: true,
        isEditable: true,
      },
    ].filter((c) => c.amount > 0);
  });
};

const getGroupedByType = (containers: NormalizedContainerManualAmount[]) => {
  return containers.reduce(
    (acc, container) => {
      if (!acc[container.container_type_id]) {
        acc[container.container_type_id] = [];
      }

      acc[container.container_type_id].push(container);

      return acc;
    },
    {} as Record<number, NormalizedContainerManualAmount[]>,
  );
};

type OrderfulfillmentSummaryProps = {
  orderFulfillment: OrderFulfillmentDetailFragment;
  containerTypes: ContainerType[];
};

const OrderfulfillmentSummary = ({
  orderFulfillment,
  containerTypes,
}: OrderfulfillmentSummaryProps) => {
  const single_containers = orderFulfillment.single_containers;
  const manual_amounts = normalizeManualAmount(
    [...orderFulfillment.manual_amounts, ...orderFulfillment.goodwill],
    orderFulfillment.type.toUpperCase(),
  ).flat();

  const container_stations = orderFulfillment.container_stations
    .map((station) => {
      return [
        ...station.containers,
        ...normalizeManualAmount(station.manual_amounts).flat(),
      ];
    })
    .flat();

  const containersGrouped = getGroupedByType([
    ...single_containers,
    ...manual_amounts,
    ...container_stations,
  ]);

  const containerStationTypeSet: Record<
    number,
    {
      counted: number;
      empty: number;
      countLater: number;
      sealed: number;
      sum: number;
    }
  > = {};

  orderFulfillment.container_stations.forEach((cs) => {
    if (!containerStationTypeSet[cs.item_type.id]) {
      containerStationTypeSet[cs.item_type.id] = {
        counted: 0,
        empty: 0,
        countLater: 0,
        sealed: 0,
        sum: 0,
      };
    }

    if (cs.status === ContainerStationStatus.Empty) {
      containerStationTypeSet[cs.item_type.id].empty += 1;
    }

    if (cs.status === ContainerStationStatus.Counted) {
      containerStationTypeSet[cs.item_type.id].counted += 1;
    }

    if (cs.status === ContainerStationStatus.Sealed) {
      containerStationTypeSet[cs.item_type.id].sealed += 1;
    }

    if (cs.status === ContainerStationStatus.Uncounted) {
      containerStationTypeSet[cs.item_type.id].countLater += 1;
    }

    containerStationTypeSet[cs.item_type.id].sum += 1;
  });

  const itemTypeCounts: Record<number, ContainerTypeCountsType> = {};

  Object.entries(containerStationTypeSet).forEach(
    ([id, { counted, empty, countLater, sealed, sum }]) => {
      itemTypeCounts[parseInt(id)] = {
        dirty: 0,
        clean: 0,
        counted,
        countLater,
        sealed,
        empty,
        sum,
        type: ItemTypeCategory.ContainerStation,
      };
    },
  );

  // Process trackable items/assets - group by item_type.id
  const assetsGrouped: Record<number, number> = {};
  orderFulfillment.assets.forEach((asset) => {
    const itemTypeId = asset.item_type.id;
    assetsGrouped[itemTypeId] = (assetsGrouped[itemTypeId] || 0) + 1;
  });

  Object.entries(assetsGrouped).forEach(([itemTypeId, count]) => {
    const id = parseInt(itemTypeId);
    if (!itemTypeCounts[id]) {
      itemTypeCounts[id] = {
        dirty: 0,
        clean: 0,
        counted: 0,
        countLater: 0,
        sealed: 0,
        empty: 0,
        sum: 0,
        type: 'TRACKABLE_ITEM',
      };
    }
    itemTypeCounts[id].sum += count;
  });

  Object.entries(containersGrouped).forEach(([id, container]) => {
    const idNumber = parseInt(id, 10);
    if (!itemTypeCounts[idNumber]) {
      itemTypeCounts[idNumber] = {
        dirty: 0,
        clean: 0,
        counted: 0,
        countLater: 0,
        sealed: 0,
        empty: 0,
        sum: 0,
        type: 'CONTAINER',
      };
    }

    let amountToAdd = 0;

    container.forEach((c) => {
      if (c.amount !== undefined) {
        if (c.sealed) {
          itemTypeCounts[idNumber].clean += c.amount;
          amountToAdd += c.amount;
        } else {
          itemTypeCounts[idNumber].dirty += c.amount;
          amountToAdd += c.amount;
        }
      } else {
        // is single unique container
        amountToAdd += 1;
      }
    });

    itemTypeCounts[idNumber].sum += amountToAdd;
  });

  const sortedContainerTypes = [...Object.entries(itemTypeCounts)].sort(
    (a, b) => parseInt(a[0]) - parseInt(b[0]),
  );

  return (
    <>
      <Table striped hover bordered>
        <tbody>
          {sortedContainerTypes.map(([idString, counts]) => {
            const { dirty, clean, counted, countLater, sealed, empty, sum } =
              counts;
            const isContainerStation = counts.type === 'CONTAINER_STATION';
            const isTrackableItem = counts.type === 'TRACKABLE_ITEM';
            const id = parseInt(idString, 10);

            return (
              <tr key={id}>
                <td>
                  {isContainerStation && (
                    <TrackableItemDisplay
                      trackableItem={orderFulfillment.container_stations.find(
                        (cs) => {
                          return cs.item_type.id === id;
                        },
                      )}
                      showTrackableItemId={false}
                      showLink={false}
                      showTypeInsteadOfName={true}
                      size={'s'}
                    />
                  )}
                  {isTrackableItem &&
                    (() => {
                      const asset = orderFulfillment.assets.find(
                        (asset) => asset.item_type.id === id,
                      );
                      return asset ? (
                        <TrackableItemDisplay
                          trackableItem={asset as TrackableItem}
                          showTrackableItemId={false}
                          showLink={false}
                          showTypeInsteadOfName={true}
                          size={'s'}
                        />
                      ) : null;
                    })()}
                  {!isContainerStation && !isTrackableItem && (
                    <ContainerTypeDisplay
                      containerType={containerTypes.find((ct) => {
                        if (isNaN(Number(id))) {
                          return ct.id === 248;
                        }

                        return ct.id === Number(id);
                      })}
                      showContainerId={false}
                      size="s"
                    />
                  )}
                </td>
                <td className="numbers-formatted">
                  {getNumberOrFallback(clean)}
                </td>
                <td className="numbers-formatted">
                  {getNumberOrFallback(dirty)}
                </td>
                <td className="numbers-formatted">
                  {getNumberOrFallback(counted)}
                </td>
                <td className="numbers-formatted">
                  {getNumberOrFallback(countLater)}
                </td>
                <td className="numbers-formatted">
                  {getNumberOrFallback(sealed)}
                </td>
                <td className="numbers-formatted">
                  {getNumberOrFallback(empty)}
                </td>
                <td className="numbers-formatted">
                  {getNumberOrFallback(sum)}
                </td>
              </tr>
            );
          })}
        </tbody>

        <thead>
          {/* This is below tbody on purpose! To have the <th> sticky above the tbody. */}
          <tr>
            <th className="sticky-table-header">{i18n.t('Common:item')}</th>
            <th className="sticky-table-header">{i18n.t('Common:clean')}</th>
            <th className="sticky-table-header">{i18n.t('Common:dirty')}</th>
            <th className="sticky-table-header">{i18n.t('Common:counted')}</th>
            <th className="sticky-table-header">
              {i18n.t('Common:countLater')}
            </th>
            <th className="sticky-table-header">{i18n.t('Common:sealed')}</th>
            <th className="sticky-table-header">{i18n.t('Common:empty')}</th>
            <th className="sticky-table-header">{i18n.t('Common:sum')}</th>
          </tr>
        </thead>
      </Table>
    </>
  );
};

export default OrderfulfillmentSummary;
